export function getCookiesMap() {
  const cookies = document.cookie
    .split(";")
    .reduce((cookieObj, cookieString) => {
      const [name, value] = cookieString.split("=").map((c) => c.trim());
      if (name) {
        cookieObj[name] = decodeURIComponent(value);
      }
      return cookieObj;
    }, {});
  return cookies;
}

function hasAuthorization(host) {
  if (host === "localhost") return true;
  const cookies = getCookiesMap();
  delete cookies["_dd_s"]; // Remove datadog RUM cookies
  delete cookies["_dd_s_v2"];
  if (Object.keys(cookies).length > 0) {
    return true;
  }
  return false;
}

// Guard the module-load `window` access so this module is safe to import in a
// non-browser (node) test environment — e.g. suites that import it transitively
// via ServiceHelper. In a real browser `window` is always present, so behavior
// is unchanged; without a window there is no session, so `auth` is false.
export const auth =
  typeof window !== "undefined"
    ? hasAuthorization(window.location.hostname)
    : false;

/**
 * Name/value of the JS-readable marker cookie that records that the current
 * session was established via the Cognito login path (`/login`), as opposed to
 * the Canvas/LTI launch flow.
 *
 * Both flows mint the same `ddl` (HttpOnly) + `ddl_o_t` session cookies with
 * identical names and values, so neither is a reliable discriminator. This
 * marker is the intentional, client-detectable signal: it is set ONLY by the
 * Cognito token->cookie exchange (see `markCognitoSession`, called from
 * `views/Login.js`) and cleared on sign-out (see `clearCognitoSessionMarker`,
 * called from `clearCognitoSession` in `utils/amplifyConfig.js`). An LTI session
 * never sets it, so its absence marks a Canvas/LTI (or unauthenticated) session.
 *
 * It is deliberately NOT HttpOnly (this JS must read it) and carries no session
 * material — it is a boolean-ish flow tag only.
 */
const COGNITO_SESSION_COOKIE = "ddl_auth";
const COGNITO_SESSION_VALUE = "cognito";

/**
 * Records that the current session was established via the Cognito login path,
 * by setting the `ddl_auth=cognito` marker cookie (session-scoped, `Path=/`,
 * `Secure` off localhost). Called from the Cognito exchange in `views/Login.js`
 * immediately before it navigates to the approved destination, so the marker is
 * present on the first request after login.
 */
export function markCognitoSession(host = window.location.hostname) {
  const secure = host === "localhost" ? "" : ";Secure";
  document.cookie = `${COGNITO_SESSION_COOKIE}=${COGNITO_SESSION_VALUE};Path=/${secure}`;
}

/**
 * Clears the Cognito-session marker cookie (by expiring it) so a subsequent
 * session is not misclassified as Cognito. Called from the sign-out path
 * (`clearCognitoSession` in `utils/amplifyConfig.js`), alongside the Amplify
 * `signOut()`.
 */
export function clearCognitoSessionMarker(host = window.location.hostname) {
  const secure = host === "localhost" ? "" : ";Secure";
  document.cookie = `${COGNITO_SESSION_COOKIE}=;Path=/;Max-Age=0${secure}`;
}

/**
 * Whether the current session was established via the Cognito login path,
 * detected by the presence of the `ddl_auth=cognito` marker cookie. Used by the
 * shared request helper's 401 handler to route Cognito sessions to the `/login`
 * page while leaving Canvas/LTI sessions on their prior session-expiry behavior.
 *
 * @returns {boolean} true iff the Cognito-session marker cookie is present
 */
export function isCognitoSession() {
  return getCookiesMap()[COGNITO_SESSION_COOKIE] === COGNITO_SESSION_VALUE;
}
